import { useState } from "react";
import { AlertTriangle, Loader2, Undo2 } from "lucide-react";

import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { undoNtsSignover } from "../lib/api";
import { salvageShort, tenantLocale, tenantTz } from "../lib/company";
import { toast } from "sonner";

/** Everything the operator should know BEFORE reversing, rather than after.
 * The owner chose "warn, don't block" — so none of these stop the undo. */
const warningsFor = (job) => {
  const out = [];
  if (job.nts_disposed_at) {
    out.push("This vehicle is marked disposed — that record will be cleared too.");
  }
  if ((job.nts_paperwork?.length || 0) > 0) {
    out.push(
      `${job.nts_paperwork.length} signed sign-over document(s) are attached and will stay on the job.`,
    );
  }
  if (!job.nts_signover_snapshot) {
    out.push(
      "This sign-over predates undo support, so the original write-off figure and overstay days cannot be restored — check the stored days afterwards.",
    );
  }
  const signed = job.released_to_nts_at ? new Date(job.released_to_nts_at) : null;
  const now = new Date();
  if (signed && (signed.getMonth() !== now.getMonth()
      || signed.getFullYear() !== now.getFullYear())) {
    out.push(
      `It was signed over in ${signed.toLocaleString(tenantLocale(), { month: "long", year: "numeric", timeZone: tenantTz() })} — if that month-end P&L has been issued, the write-off figure on it will no longer match.`,
    );
  }
  return out;
};

export const UndoSignoverDialog = ({ job, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!job) return null;
  const warnings = warningsFor(job);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await undoNtsSignover(job.id, reason.trim());
      toast.success(
        `${job.rego || job.vehicle || "Vehicle"} is back in the yard`,
        { description: res.warnings?.join(" ") || undefined },
      );
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reverse the sign-over");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="undo-signover-dialog">
        <DialogHeader>
          <DialogTitle>Undo sign-over</DialogTitle>
          <DialogDescription>
            {job.vehicle || job.rego || "This vehicle"} goes back to being
            {job.source === "impound" ? " an impound" : " a stored vehicle"} in
            your yard, and off the {salvageShort()} inventory.
          </DialogDescription>
        </DialogHeader>

        {warnings.length > 0 && (
          <ul
            className="space-y-1.5 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            data-testid="undo-signover-warnings"
          >
            {warnings.map((w) => (
              <li key={w} className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="undo-reason" className="uppercase-label">
            Reason (shows in the job history)
          </Label>
          <Textarea
            id="undo-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. signed over by mistake — owner is paying"
            data-testid="undo-signover-reason"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="undo-signover-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || reason.trim().length < 3}
            className="bg-amber-600 text-white hover:bg-amber-700"
            data-testid="undo-signover-confirm"
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  : <Undo2 className="mr-1 h-4 w-4" />}
            Put it back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UndoSignoverDialog;
